/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "semantic-ui-react";
import { selectEditMode } from "../../slices/editMode/editModeSlice";
import { resourceUpdated, selectAllResources, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { changeTopNavigationElements } from "../../slices/navigationPanel/navigationPanelSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { loadTopSections, selectAllTopSections, selectTopSectionsStatus, topSectionAdded } from "../../slices/topSections/topSectionsSlice";
import jsonEquals from "../../functions/jsonEquals";
import { ReorganizeSectionsModal } from "../ReorderCompositionSections";
import { simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import TopSectionSliceDisplay from "./TopSectionSliceDisplay";
import Spinner from "../../components/Spinner";
import { changeComparativeEvidenceMap, selectComparativeEvidenceMap } from "../../slices/comparativeEvidenceMap/comparativeEvidenceMapSlice";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import NarrativeEntry from "../../datatypeComponents/NarrativeEntry";
import generateM11Narrative from "../compositionResourceFunctions/generateM11Narrative";
import { updateNarrativeElement } from "../../functions/updateElement";
import { getThePDF } from "./CompositionPdfViewer";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import ViewPdf from "../../components/ViewPdfLauncher";
import checkM11Section from "../m11ReportFunctions/m11SectionValidator";
import { validateStep } from "../../functions/validateM11ReportAPI";
import { getTheM11PDF } from "../m11ReportComponents/M11Step045CreateM11Report";
import AlertModal from "../../components/AlertModal";
import { m11ResearchStudyFoiChanged, selectM11ReportContainerFoi, selectM11ReportContainerResource, selectM11ResearchStudyFoi } from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import { fetchTheSet } from "../m11ReportComponents/M11ReportAuthoringTool";

// eslint-disable-next-line no-empty-pattern
const M11ReportPdfViewer = ({ setAlertModalState }) => {
    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ReportContainerResource = useSelector(selectM11ReportContainerResource);
    const m11ReportContainerFoi = useSelector(selectM11ReportContainerFoi);

    const [pdfState, setPdfState] = useState();
    const [docxState, setDocxState] = useState();

    let pdfFileName = m11ReportContainerResource.id + " - " + (m11ReportContainerResource.title ? m11ReportContainerResource.title.substring(0, 60) : "Unnamed");

    const handleViewPdf = () => {
        if (m11ReportContainerFoi) {
            getTheM11PDF(dispatch, apiToken, pdfState, setPdfState, docxState, setDocxState, setAlertModalState, false, null, m11ReportContainerFoi, false);
        }
    };

    return <div>{m11ReportContainerFoi && <>
        <div className="unselectable">
            <br />
            <ViewPdf label="View PDF" pdfData={pdfState} onLoad={handleViewPdf} />
            <button type="button" style={{ marginLeft: "12px" }}
                onClick={() => { getTheM11PDF(dispatch, apiToken, pdfState, setPdfState, docxState, setDocxState, setAlertModalState, true, pdfFileName, m11ReportContainerFoi, false); }}
            >{"Download PDF"}</button>
            <button type="button" style={{ marginLeft: "12px" }}
                onClick={() => { getTheM11PDF(dispatch, apiToken, pdfState, setPdfState, docxState, setDocxState, setAlertModalState, true, pdfFileName, m11ReportContainerFoi, true); }}
            >{"Download DOCX"}</button>
            <br />    <br />
        </div>
    </>}
    </div>;
};

// eslint-disable-next-line no-empty-pattern
const M11Step045CreateM11ReportCopy = ({ setAlertModalState }) => {
    const theM11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);
    const m11ReportContainerFoi = useSelector(selectM11ReportContainerFoi);
    return <div>
        {m11ReportContainerFoi && <>
            <h3>Generate the M11 Report</h3>
            <M11ReportPdfViewer setAlertModalState={setAlertModalState} />
            View the <a href={"/" + theM11ResearchStudyFoi} target="_blank" rel="noopener noreferrer">M11ResearchStudy for FOI {theM11ResearchStudyFoi}</a>
        </>}
    </div>
};

const CompositionBasicDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    const allTopSections = useSelector(selectAllTopSections);
    const topSectionsStatus = useSelector(selectTopSectionsStatus);
    const comparativeEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const resourceCollection = useSelector(state => selectAllResources(state));

    let isEvidenceReportProfile;
    let isResearchStudyDataDictionaryProfile;
    let isEvidenceReportPackageProfile;
    let isGuidelineProfile;
    let isGuidelineRegistryRecordProfile;
    let isRecommendationProfile;
    let isM11ReportProfile;
    let isM11ReportContainerProfile;
    if (resourceJson.meta?.profile?.length) {
        for (const profile of resourceJson.meta.profile) {
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/evidence-report") {
                isEvidenceReportProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-data-dictionary") {
                isEvidenceReportProfile = true;
                isResearchStudyDataDictionaryProfile = true;
            }
            if (profile.includes("http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report")) {
                isEvidenceReportProfile = true;
                isM11ReportProfile = true;
                if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-container") {
                    isM11ReportContainerProfile = true;
                }
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/evidence-report-package") {
                isEvidenceReportProfile = true;
                isEvidenceReportPackageProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/guideline") {
                isEvidenceReportProfile = true;
                isEvidenceReportPackageProfile = true;
                isGuidelineProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/guideline-registry-record") {
                isEvidenceReportProfile = true;
                isEvidenceReportPackageProfile = true;
                isGuidelineProfile = true;
                isGuidelineRegistryRecordProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/recommendation") {
                isEvidenceReportProfile = true;
                isEvidenceReportPackageProfile = true;
                isRecommendationProfile = true;
            }
        }
    }

    const [compositionResourceState, setCompositionResourceState] = useState({});
    //const [extensionState, setExtensionState] = useState({});
    //TODO - for future use to create generated Narrative
    //const [generatedNarrativeState, setGeneratedNarrativeState] = useState(""); 
    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });
    const [sectionNavigationElementIds, setSectionNavigationElementIds] = useState([]);
    const [sectionsReorganizedState, setSectionsReorganizedState] = useState({});
    const [addSectionState, setAddSectionState] = useState({
        "sectionTitle": "", "addSectionClicked": false
    });
    const [m11ValidationState, setM11ValidationState] = useState({ "issues": [] });
    const [pdfState, setPdfState] = useState();
    const [jsonHashState, setJsonHashState] = useState();
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});
    const [validationCheckState, setValidationCheckState] = useState({});
    const [m11ResearchStudyFoiState, setM11ResearchStudyFoiState] = useState("");
    const [alertModalState, setAlertModalState] = useState({
        open: false, alertHistory: [], header: "", content: ""
    });

    const handleViewPdf = useCallback(() => {
        getThePDF(dispatch, apiToken, compositionResourceState, pdfState, setPdfState, jsonHashState, setJsonHashState, false, null, foi, true);
    }, [dispatch, compositionResourceState, pdfState, setPdfState, jsonHashState, setJsonHashState]);

    useEffect(() => {
        //console.log("CompositionBasicDisplay useEffect due to change in resourceJson")
        if (resourceJson?.id ||
            (viewingOlderVersion && olderVersionFhirString)) {
            if (viewingOlderVersion && olderVersionFhirString) {
                try {
                    const olderVersionJson = JSON.parse(olderVersionFhirString);
                    setCompositionResourceState(olderVersionJson);
                    let topNavigationElements = [];
                    let sectionHeaderNavigationElements = [];
                    if (isM11ReportProfile) {
                        topNavigationElements.push("Narrative Summary");
                        sectionHeaderNavigationElements.push("Narrative Summary");
                    }

                    if (Array.isArray(olderVersionJson.section)) {
                        for (const section of olderVersionJson.section) {
                            let sectionCode = section?.code?.coding?.[0]?.display || section?.code?.text || section?.code?.coding?.[0]?.code;
                            let sectionFocus = section?.focus?.display || section?.focus?.reference;
                            let sectionHeader = section.title || sectionCode || sectionFocus || "Unidentified Section";
                            topNavigationElements.push(sectionHeader);
                            sectionHeaderNavigationElements.push(sectionHeader);
                        }
                    }
                    dispatch(changeTopNavigationElements(topNavigationElements));
                    const sectionNavigationIds = sectionHeaderNavigationElements.map(((element) => {
                        let id = element.toLowerCase().replace(/\s/g, "-").replace(/\//g, "-");
                        return id;
                    }))
                    setSectionNavigationElementIds(sectionNavigationIds);
                } catch {
                    alert("Error viewing JSON of older version.");
                }
            } else if (resourceJson?.id) {
                setCompositionResourceState(JSON.parse(JSON.stringify(resourceJson)));
                setSectionsReorganizedState({ "reorganized": false, "section": resourceJson.section ? JSON.parse(JSON.stringify(resourceJson.section)) : [] });
                let topNavigationElements = [];
                let sectionHeaderNavigationElements = [];
                let picoClassifiers = {};
                let populationArray = [];
                let interventionArray = [];
                let comparatorArray = [];
                let outcomeArray = [];
                if (isM11ReportProfile) {
                    topNavigationElements.push("Narrative Summary");
                    sectionHeaderNavigationElements.push("Narrative Summary");
                    if (resourceJson.relatesTo?.length > 0) {
                        for (const relatesTo of resourceJson.relatesTo) {
                            if (relatesTo.type?.coding?.[0]?.code === "supports" &&
                                relatesTo.targetReference?.type === "ResearchStudy" &&
                                relatesTo.targetReference.reference?.startsWith("ResearchStudy/")
                            ) {
                                let theFoi = relatesTo.targetReference.reference.replace("ResearchStudy/", "");
                                if (!isNaN(theFoi)) {
                                    setM11ResearchStudyFoiState(theFoi);
                                    if (isM11ReportContainerProfile) {
                                        dispatch(m11ResearchStudyFoiChanged(theFoi));
                                    }
                                    break;
                                }
                            }
                        }
                    }
                }
                if (Array.isArray(resourceJson.useContext) && resourceJson.useContext.length > 0) {
                    for (const item of resourceJson.useContext) {
                        const code = item?.code.code;
                        if (code === "population" && item.valueCodeableConcept) {
                            populationArray.push({ "concept": item.valueCodeableConcept });
                        }
                        else if (code === "intervention" && item.valueCodeableConcept) {
                            interventionArray.push({ "concept": item.valueCodeableConcept });
                        }
                        else if (code === "comparator" && item.valueCodeableConcept) {
                            comparatorArray.push({ "concept": item.valueCodeableConcept });
                        }
                        else if (code === "outcome-measure" && item.valueCodeableConcept) {
                            outcomeArray.push({ "concept": item.valueCodeableConcept });
                        }
                    }
                }
                if (Array.isArray(resourceJson.classification) && resourceJson.classification.length > 0) {
                    for (const item of resourceJson.classification) {
                        const code = item?.type?.coding?.[0]?.code;
                        if (code === "population" && item.classifer) {
                            populationArray = item.classifer;
                        }
                        else if (code === "intervention" && item.classifer) {
                            interventionArray = item.classifer;
                        }
                        else if (code === "comparator" && item.classifer) {
                            comparatorArray = item.classifer;
                        }
                        else if (code === "outcome-measure" && item.classifer) {
                            outcomeArray = item.classifer;
                        }
                    }
                }
                picoClassifiers = {
                    populationArray: populationArray,
                    interventionArray: interventionArray,
                    comparatorArray: comparatorArray,
                    outcomeArray: outcomeArray
                };
                if (Array.isArray(resourceJson.section)) {
                    for (const section of resourceJson.section) {
                        let sectionCode = section?.code?.coding?.[0]?.display || section?.code?.text || section?.code?.coding?.[0]?.code;
                        let sectionFocus = section?.focus?.display || section?.focus?.reference;
                        let sectionHeader = section?.title || sectionCode || sectionFocus || "Unidentified Section";
                        topNavigationElements.push(sectionHeader);
                        sectionHeaderNavigationElements.push(sectionHeader);
                    }
                }
                let resourceReferenceMap = {
                    "compositionId": resourceJson.id,
                    "compositionTitle": resourceJson.title || resourceJson.name || parentResourceDetail.displayTitle
                };

                let combinedDictionary = {};
                for (const entry of resourceCollection) {
                    combinedDictionary[entry.id] = entry.resourceJson;
                }
                if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                    let containedResourceDictionary = {};
                    for (const item of resourceJson.contained) {
                        combinedDictionary[item.id] = item;
                        const key = "#" + item.id;
                        containedResourceDictionary[key] = item;
                    }
                    setContainedResourceDictionary(containedResourceDictionary);
                }
                resourceReferenceMap["combinedDictionary"] = combinedDictionary;
                resourceReferenceMap["picoClassifiers"] = picoClassifiers;
                dispatch(changeComparativeEvidenceMap(resourceReferenceMap));
                dispatch(changeTopNavigationElements(topNavigationElements));
                const sectionNavigationIds = sectionHeaderNavigationElements.map(((element) => {
                    let id = element.toLowerCase().replace(/\s/g, "-").replace(/\//g, "-");
                    return id;
                }))
                setSectionNavigationElementIds(sectionNavigationIds);
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        if (isM11ReportContainerProfile && m11ResearchStudyFoiState && apiToken) {
            fetchTheSet(m11ResearchStudyFoiState, dispatch, apiToken);
        }
    }, [m11ResearchStudyFoiState, dispatch, apiToken]);

    useEffect(() => {
        if (m11ResearchStudyFoiState) {
            validateStep(apiToken, 44, m11ResearchStudyFoiState,
                setValidationCheckState)
        }
    }, [apiToken, m11ResearchStudyFoiState]);

    useEffect(() => {
        if (isM11ReportProfile && Object.keys(validationCheckState).length > 0) {
            let m11IssuesList = [];
            if (Array.isArray(resourceJson.section)) {
                for (const section of resourceJson.section) {
                    checkM11Section(section, m11IssuesList, validationCheckState);
                }
            }
            setM11ValidationState({ "issues": m11IssuesList });
        }
    }, [validationCheckState]);

    useEffect(() => {
        if (topSectionsStatus === "succeeded" && editMode && resourceJson && allTopSections?.length > 0) {
            let previousInitialValue = parentResourceDetail.resourceJson.section;
            let previousFinalValue = parentResourceDetail.revisedResourceJson.section;
            let newCompositionSection = allTopSections.map(entity => { return entity.revisedSection });
            if ((previousInitialValue || newCompositionSection.length !== 1 || newCompositionSection[0]) //Is the intention here to check to see if it has keys inside, or just to make sure it's not undefined/null?
                && !jsonEquals(previousFinalValue, newCompositionSection)) {
                console.log("resourceUpdate for Composition.section");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.section = newCompositionSection;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [allTopSections]);

    useEffect(() => {
        if (editMode && resourceJson && sectionsReorganizedState.reorganized &&
            Array.isArray(sectionsReorganizedState.section) && sectionsReorganizedState.section.length > 0) {
            setSectionsReorganizedState(prevState => { return { ...prevState, "reorganized": false } });
            let oldCompositionSection = allTopSections.map(entity => { return entity.revisedSection });
            if (!jsonEquals(oldCompositionSection, sectionsReorganizedState.section)) {
                let topSectionArray = sectionsReorganizedState.section.map((section, sectionIndex) => {
                    return {
                        id: sectionIndex,
                        displayTitle: section.title ||
                            simpleDisplayStringFromFHIRCodeableConcept(section.code) ||
                            displayStringFromFHIRReference(section.focus) || "Untitled Section",
                        compositionId: foi,
                        productFamily: parentResourceDetail.productFamily,
                        initialSection: section,
                        revisedSection: section
                    };
                });
                let topSectionsBody = { topSections: topSectionArray };
                dispatch(loadTopSections(topSectionsBody));
                console.log("sectionsReorganized so resourceUpdate for Composition.section");
            }
        }
    }, [sectionsReorganizedState]);

    useEffect(() => {
        if (editMode && resourceJson && addSectionState.addSectionClicked &&
            addSectionState.sectionTitle) {
            let sectionLength = allTopSections.length;
            let sectionBody = {
                id: sectionLength,
                displayTitle: addSectionState.sectionTitle,
                compositionId: foi,
                productFamily: parentResourceDetail.productFamily,
                initialSection: { "title": addSectionState.sectionTitle },
                revisedSection: { "title": addSectionState.sectionTitle }
            }
            if (topSectionsStatus !== "succeeded") {
                dispatch(loadTopSections({ topSections: [] }));
            }
            dispatch(topSectionAdded(sectionBody));
            console.log("addSectionState so resourceUpdate for Composition.section");
            setAddSectionState({
                "sectionTitle": "", "addSectionClicked": false
            });
        }
    }, [addSectionState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(compositionResourceState, "text", parentResourceDetail,
                dispatch);
        }
    }, [compositionResourceState.text]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <>
        {compositionResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <AlertModal alertModalState={alertModalState} setAlertModalState={setAlertModalState} />
                {isM11ReportProfile &&
                    <>{/**TODO - replace with M11 Report Viewing code*/}
                        {isM11ReportContainerProfile &&
                            <M11Step045CreateM11ReportCopy setAlertModalState={setAlertModalState} />}
                        {m11ValidationState?.issues &&
                            <>
                                <h3>M11 Report Validation Check</h3>
                                <div style={{ marginLeft: "24px" }}>
                                    {m11ValidationState.issues.length > 0 ?
                                        <div style={{ color: "red" }}>
                                            {m11ValidationState.issues.map((issue, issueIndex) => {
                                                return <p key={issueIndex}>{(issueIndex + 1)}. {issue}</p>
                                            })}
                                        </div>
                                        :
                                        <div>
                                            <div style={{ color: "green" }}>
                                                <p>No issues.</p>
                                            </div>
                                            {!isM11ReportContainerProfile &&
                                                <div className="unselectable">
                                                    <ViewPdf label="View M11 Report Section PDF" pdfData={pdfState} onLoad={handleViewPdf} />
                                                    <button type="button" style={{ marginLeft: "12px" }}
                                                        onClick={() => {
                                                            let fhirJson = {
                                                                "resourceType": "Composition",
                                                                "title": compositionResourceState.title,
                                                                "section": [
                                                                    {
                                                                        "text": compositionResourceState.text
                                                                    }
                                                                ]
                                                            };
                                                            let pdfFileName = compositionResourceState.id + " - " + (compositionResourceState.title ? compositionResourceState.title.substring(0, 60) : "Unnamed");
                                                            getThePDF(dispatch, apiToken, fhirJson, pdfState, setPdfState, jsonHashState, setJsonHashState, true, pdfFileName, foi, true);
                                                        }}
                                                    >{"Download M11 Report Section PDF"}</button>
                                                    <br />
                                                </div>}
                                        </div>
                                    }
                                </div>
                                {isM11ReportContainerProfile && <div style={{ marginLeft: "24px" }}></div>}
                            </>}
                        {/*Replace the pdfState and getThePDF and possibly handleViewPdf values below with something that compiles the fully desired M11 Report PDF with all section headers from the reference section Resources as table of contents and also calculates page numbers for the compiled PDF to include in the table of contents*/}
                        <h3>Narrative Summary</h3>
                        <div style={{ marginLeft: "24px" }}>
                            {editMode ?
                                <>
                                    <NarrativeEntry elementName="text"
                                        startingValue={compositionResourceState.text}
                                        calledFrom={isM11ReportProfile && "M11Report"}
                                        generatedNarrative={generateM11Narrative(compositionResourceState)}
                                        setResourceState={setCompositionResourceState} />
                                </>
                                :
                                <>{(compositionResourceState.text?.div &&
                                    compositionResourceState.text.status !== "empty") ?
                                    <>
                                        <ReactMarkdown children={compositionResourceState.text.div}
                                            rehypePlugins={[rehypeRaw]} />
                                        <br />
                                    </>
                                    :
                                    <div>
                                        <p>[No Narrative summary.]</p>
                                        <br />
                                    </div>}</>
                            }
                        </div>
                    </>}
                {editMode && <div>
                    <Button className="formButton" style={{ margin: "0px", /*color: "#000000", */ }}
                        content="Reorganize Sections" compact
                        onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                    <br />
                </div>}
                {(editSectionModal.openModal) &&
                    <>
                        <ReorganizeSectionsModal
                            editSectionModal={editSectionModal}
                            setEditSectionModal={setEditSectionModal}
                            sectionsReorganizedState={sectionsReorganizedState}
                            setSectionsReorganizedState={setSectionsReorganizedState}
                        />
                    </>}
                {(compositionResourceState.section?.length > 0) &&
                    compositionResourceState.section.map((section, sectionIndex) => {
                        return <div key={sectionIndex}>
                            <TopSectionSliceDisplay foi={foi} containedResourceDictionary={containedResourceDictionary}
                                setCompositionResourceState={setCompositionResourceState}
                                sectionIndex={sectionIndex} isM11ReportProfile={isM11ReportProfile}
                                resourceReferenceMap={comparativeEvidenceMap}
                                sectionNavigationElementIds={sectionNavigationElementIds}
                            />
                            <br />
                        </div>
                    })}
                {editMode &&
                    <>
                        <Button style={{ color: "#000000", width: "130px", float: "left" }}
                            className="formButton"
                            content="+ Add Section"
                            onClick={() => {
                                setAddSectionState({
                                    "sectionTitle": "Replace with Proper Section Title",
                                    "addSectionClicked": true
                                });
                            }}
                        />
                    </>}
            </div>
        </>}
    </>
}

export default CompositionBasicDisplay;